import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { login, mfaVerify, startSso, status } from "../api/auth.ts";

export default function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { data } = useQuery({ queryKey: ["auth", "status"], queryFn: status });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (data?.setup_required) return <Navigate to="/setup" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await login({ email, password, totp_code: totp || undefined, device_label: navigator.userAgent.slice(0, 120) });
      if (r.mfa_required && r.mfa_challenge_token) {
        setChallenge(r.mfa_challenge_token);
        return;
      }
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(String((err as Error).message));
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mfaVerify(challenge!, totp, trustDevice);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(String((err as Error).message));
    }
  };

  const sso = async () => {
    const { authorization_url } = await startSso();
    window.location.href = authorization_url;
  };

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <form onSubmit={challenge ? verify : submit} className="card w-full max-w-md space-y-4">
        <h1 className="text-2xl font-semibold">{challenge ? t("auth.2fa.title") : t("auth.login.title")}</h1>
        {!challenge && (
          <>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t("auth.login.email")}</span>
              <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t("auth.login.password")}</span>
              <input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
          </>
        )}
        {challenge && (
          <>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">6-digit code or recovery code</span>
              <input className="input" value={totp} onChange={(e) => setTotp(e.target.value)} autoFocus required />
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand-600"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              Remember this device for 30 days
            </label>
          </>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="submit" className="btn w-full">
          {challenge ? t("auth.2fa.submit") : t("auth.login.submit")}
        </button>
        {!challenge && (
          <button type="button" className="btn-secondary w-full" onClick={sso}>
            {t("auth.login.sso")}
          </button>
        )}
      </form>
    </div>
  );
}
